<template>
  <div class="mobileViewStyle opportunity-detail">
    <!-- 商机详情：查看模式（基本信息 + 新增跟进 + 跟进记录）/ 编辑模式（商机本身的信息） -->
    <div class="detail-body">
      <!-- ==================== 查看模式 ==================== -->
      <template v-if="isView">
        <!-- 基本信息：右上角「编辑」切到编辑模式 -->
        <list-card :label="detail.opportunityTitle">
          <template #desc>
            <span class="stage-tag" :class="stageClass(detail.currentStage)">{{ stageName(detail.currentStage) }}</span>
            <span class="edit-btn" @click="toEdit">编辑</span>
          </template>
          <data-column label="商机编号">{{ detail.opportunityNo || "-" }}</data-column>
          <data-column label="商机金额">{{ money(detail.opportunityAmount) }}</data-column>
          <data-column label="成交概率">{{ (detail.winRate || 0) + "%" }}</data-column>
          <data-column label="预计成交">{{ formatDate(detail.expectDealDate) }}</data-column>
          <data-column label="实际成交">{{ formatDate(detail.actualDealDate) }}</data-column>
          <data-column label="负责人">{{ detail.ownerEmployeeNames || "-" }}</data-column>
          <data-column label="商机来源">{{ sourceName(detail.sourceDictId) || "-" }}</data-column>
          <data-column v-if="detail.closeReason" label="输单原因">{{ detail.closeReason }}</data-column>
          <data-column label="备注">{{ detail.remark || "-" }}</data-column>
          <mobile-partner-wx-bind :partner-id="detail.partnerId"/>
        </list-card>

        <!-- 跟进记录的新增区域 -->
        <list-card label="新增跟进" type="form">
          <data-input label="跟进内容" v-model="followData.followContent" placeholder="请输入本次沟通的情况"/>
          <data-input label="商机阶段">
            <mobile-select class="row-select" :clearable="false" v-model="followData.stageValue" placeholder="请选择阶段">
              <mobile-option v-for="s in stageList" :key="s.userCode" :label="s.dictName" :value="Number(s.userCode)"/>
            </mobile-select>
          </data-input>
          <data-input label="跟进时间">
            <mobile-date-select v-model="followData.followTime" placeholder="请选择日期"/>
          </data-input>
          <data-input label="下次跟进">
            <mobile-date-select v-model="followData.nextFollowDate" placeholder="请选择日期"/>
          </data-input>
        </list-card>
        <div class="follow-save">
          <mobile-button el-type="primary" @click="saveFollow">保存跟进</mobile-button>
        </div>

        <!-- 跟进记录：后端已按跟进时间倒序返回 -->
        <list-card v-if="followList.length" :label="'跟进记录（' + followList.length + '）'" type="form">
          <div class="follow-item" v-for="item in followList" :key="item.id">
            <div class="follow-head">
              <span class="who">{{ item.followEmployeeName || "—" }}</span>
              <span class="stage-tag" v-if="stageName(item.stageValue)" :class="stageClass(item.stageValue)">{{ stageName(item.stageValue) }}</span>
              <span class="time">{{ formatDate(item.followTime) }}</span>
            </div>
            <div class="follow-content">{{ item.followContent }}</div>
            <div class="follow-next" v-if="item.nextFollowDate">下次跟进：{{ formatDate(item.nextFollowDate) }}</div>
          </div>
        </list-card>
        <div v-else class="follow-empty">暂无跟进记录</div>
      </template>

      <!-- ==================== 编辑模式 ==================== -->
      <list-card v-else label="商机信息" type="form">
        <data-input label="商机名称" v-model="detail.opportunityTitle" placeholder="如：某某公司年度采购商机"/>
        <data-input label="当前阶段">
          <mobile-select class="row-select" :clearable="false" v-model="detail.currentStage" placeholder="请选择阶段">
            <mobile-option v-for="s in stageList" :key="s.userCode" :label="s.dictName" :value="Number(s.userCode)"/>
          </mobile-select>
        </data-input>
        <data-input label="商机金额(元)" v-model="detail.opportunityAmount" desc="" placeholder="请输入金额"/>
        <data-input label="成交概率(%)" v-model="detail.winRate" desc="" placeholder="0 - 100"/>
        <data-input label="负责人">
          <mobile-employee-select v-model="detail.ownerEmployeeIds" :display-text="detail.ownerEmployeeNames"/>
        </data-input>
        <data-input label="商机来源">
          <mobile-condition-select v-model="detail.sourceDictId" :data="sourceList"
                                   label-column="dictName" code-column="userCode" type="list"
                                   placeholder="请选择来源"/>
        </data-input>
        <data-input label="预计成交">
          <mobile-date-select v-model="detail.expectDealDate" placeholder="请选择日期"/>
        </data-input>
        <!-- 阶段选到「已流失」时必填输单原因 -->
        <data-input v-if="isLostStage" label="输单原因" v-model="detail.closeReason" placeholder="请说明输单原因"/>
        <data-input label="备注" v-model="detail.remark" placeholder="选填"/>
        <!-- 新建商机也可从企业微信选择客户：只回填名称，保存商机后再补关联 -->
        <mobile-partner-wx-bind ref="wxBind" :partner-id="detail.partnerId"
                                :allow-import="!route.query.id" @imported="onWxImported"/>
      </list-card>
    </div>

    <div class="detail-footer" v-if="!isView">
      <mobile-button @click="cancelEdit">取消</mobile-button>
      <mobile-button el-type="primary" @click="saveData">保存</mobile-button>
    </div>
  </div>
</template>

<script>

import {useRoute, useRouter} from "vue-router";
import ListCard from "@/components/qywx/layout/ListCard.vue";
import DataInput from "@/components/qywx/data/DataInput.vue";
import DataColumn from "@/components/qywx/data/DataColumn.vue";
import MobileSelect from "@/components/qywx/data/MobileSelect.vue";
import MobileOption from "@/components/qywx/data/MobileOption.vue";
import MobileConditionSelect from "@/components/qywx/data/MobileConditionSelect.vue";
import MobileEmployeeSelect from "@/components/qywx/data/MobileEmployeeSelect.vue";
import MobileDateSelect from "@/components/qywx/data/MobileDateSelect.vue";
import MobileButton from "@/components/qywx/button/MobileButton.vue";
import MobilePartnerWxBind from "@/components/qywx/partner/MobilePartnerWxBind.vue";
import opportunityApi from "@/api/dz-crm/partner/opportunityApi";
import commonApi from "@/api/commonApi";
import util from "@/util/util";
import {
  DICT_OPPORTUNITY_STAGE, DICT_PARTNER_ORIGIN, STAGE, dictName, stageTagType
} from "@/views/h5/crm/crmDictionary";

/** 取本地当天的 yyyy-MM-dd。不能用 toISOString()：它是 UTC，东八区凌晨会退到前一天 */
function today() {
  const d = new Date();
  const pad = n => String(n).padStart(2, "0");
  return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
}

export default {
  setup() {
    return {
      router: useRouter(),
      route: useRoute(),
    }
  },
  name: "OD",
  components: {
    MobilePartnerWxBind, MobileButton, MobileEmployeeSelect, MobileDateSelect, MobileConditionSelect, MobileOption, MobileSelect,
    DataColumn, DataInput, ListCard
  },
  data() {
    return {
      mode: "edit",   // view（查看）/ edit（编辑）；新建时为 edit
      detail: {},
      stageList: [],
      sourceList: [],
      followData: {
        partnerId: "",
        stageValue: "",
        followEmployeeId: "",
        followContent: "",
        // 默认当天，与 web 端一致
        followTime: today(),
        nextFollowDate: "",
      },
      pageTitle: "",
    };
  },
  computed: {
    isView() {
      return this.mode === "view";
    },
    followList() {
      return this.detail.followList || [];
    },
    // 商机阶段选到「已流失」时才要填输单原因
    isLostStage() {
      return this.detail.currentStage === STAGE.LOST;
    }
  },
  created() {
    this.pageTitle = document.title;
    document.title = this.route.query.id ? "商机详情" : "新增商机";
    commonApi.queryUniqueDictionaryList(DICT_OPPORTUNITY_STAGE).then(list => this.stageList = list || []);
    commonApi.queryUniqueDictionaryList(DICT_PARTNER_ORIGIN).then(list => this.sourceList = list || []);
    if (this.route.query.id) {
      // 点列表数据进来 = 查看模式
      this.mode = "view";
      this.findById();
    } else {
      // 新建 = 编辑模式
      this.mode = "edit";
      this.initNewOpportunity();
    }
  },
  unmounted() {
    document.title = this.pageTitle;
  },
  methods: {
    findById() {
      opportunityApi.findById(this.route.query.id).then(res => {
        this.detail = res || {};
        this.initFollowForm();
      });
    },
    // 跟进表按往来单位档案id归属 —— 用 partnerId，不是商机行主键 id
    initFollowForm() {
      this.followData = {
        partnerId: this.detail.partnerId,
        // 预填该商机当前的阶段与负责人，跟进时间默认当天（可改）
        stageValue: this.detail.currentStage,
        followEmployeeId: this.detail.ownerEmployeeId,
        followContent: "",
        followTime: today(),
        nextFollowDate: "",
      };
    },
    // 新建商机：初始化一份空表单
    initNewOpportunity() {
      // 负责人默认当前登录人（与 web 端一致）：普通角色只能看到与自己相关的数据，
      // 留空会导致刚建完的商机在列表里查不到
      const employeeId = util.getEmployeeId();
      this.detail = {
        opportunityTitle: "",
        currentStage: STAGE.CLUE,
        opportunityAmount: 0,
        winRate: 0,
        ownerEmployeeIds: employeeId,
        ownerEmployeeNames: "",
        sourceDictId: "",
        expectDealDate: "",
        closeReason: "",
        remark: "",
      };
      if (employeeId) {
        // 补负责人姓名（只用于回显）；用户已经改过负责人就不覆盖
        commonApi.queryEmployeeByIds([employeeId]).then(list => {
          const employee = list && list[0];
          if (employee && this.detail.ownerEmployeeIds === employeeId && !this.detail.ownerEmployeeNames) {
            this.detail.ownerEmployeeNames = employee.employeeName;
          }
        });
      }
    },
    // 查看模式 -> 编辑模式
    toEdit() {
      this.mode = "edit";
    },
    // 取消编辑：已有的商机回查看模式（重新拉一次，丢弃本次未保存的改动）；新建则回列表
    cancelEdit() {
      if (this.route.query.id) {
        this.mode = "view";
        this.findById();
      } else {
        this.router.back();
      }
    },
    stageName(stageValue) {
      return dictName(this.stageList, stageValue);
    },
    sourceName(sourceId) {
      return dictName(this.sourceList, sourceId);
    },
    // 阶段色：字典不给颜色，由前端按固定映射决定（见 crmDictionary 的 STAGE_TAG）
    stageClass(stageValue) {
      const type = stageTagType(stageValue);
      return type ? "tag-" + type : "tag-primary";
    },
    formatDate(value) {
      return value ? String(value).slice(0, 10) : "-";
    },
    money(value) {
      if (value === null || value === undefined || value === "") {
        return "-";
      }
      return "￥" + Number(value).toLocaleString("zh-CN", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    },
    /** 从企业微信导入商机：用客户名称回填商机名称，保存后再补关联 */
    onWxImported(contact) {
      if (!contact) {
        return;
      }
      this.detail.opportunityTitle = contact.corpName || contact.userName || this.detail.opportunityTitle;
    },
    // 保存商机本身的信息
    saveData() {
      if (!this.detail.opportunityTitle) {
        this.$message.error("请填写商机名称");
        return;
      }
      const isNew = !this.route.query.id;
      // 成功提示由请求拦截器统一弹（save*** 接口），这里不再自己弹
      opportunityApi.saveOpportunity(this.detail).then(res => {
        if (isNew && this.$refs.wxBind && res && res.partnerId) {
          return this.$refs.wxBind.bindPending(res.partnerId);
        }
      }).then(() => {
        if (isNew) {
          // 新建：回列表
          this.router.back();
        } else {
          // 编辑已有商机：回查看模式并刷新
          this.mode = "view";
          this.findById();
        }
      });
    },
    // 保存跟进记录
    saveFollow() {
      if (!this.followData.followContent) {
        this.$message.error("请填写跟进内容");
        return;
      }
      // 成功提示由请求拦截器统一弹（save*** 接口），这里不再自己弹
      opportunityApi.saveFollow(this.followData).then(() => {
        // 重新拉一次详情：跟进记录 + 商机阶段（跟进可能改了阶段）都刷新
        this.findById();
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.opportunity-detail{
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--yq-background-color);
  .detail-body{
    flex: 1;
    overflow-y: auto;
    padding: 10px;
  }
  .detail-footer{
    flex: none;
    display: flex;
    gap: 10px;
    padding: 10px;
    background: #FFF;
    box-shadow: 0 -1px 4px var(--yq-shadow);
    :deep(.mobile-button){
      flex: 1;
    }
  }
  // 跟进记录的保存按钮：贴在新增区域下方
  .follow-save{
    margin-top: 10px;
  }
  .follow-empty{
    margin-top: 10px;
    padding: 20px;
    text-align: center;
    color: var(--yq-font-color-desc);
    background: #FFF;
    border-radius: 6px;
    box-shadow: 0 0 10px var(--yq-shadow);
  }
  // 基本信息卡右上角：阶段标签 + 编辑按钮
  :deep(.card-title){
    // 商机名称通常较长，右上角又有标签和按钮，允许折两行、别一路省略
    height: auto;
    min-height: 60px;
    .name{
      white-space: normal;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
      line-height: 1.3;
    }
    .desc{
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }
  .edit-btn{
    flex: none;
    padding: 2px 10px;
    border-radius: 4px;
    font-size: var(--yq-font-size-mini-mobile);
    color: var(--el-color-primary);
    background: var(--el-color-primary-light-9);
  }
  .stage-tag{
    flex: none;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: var(--yq-font-size-mini-mobile);
    &.tag-primary{
      color: var(--el-color-primary);
      background: var(--el-color-primary-light-9);
    }
    &.tag-info{
      color: var(--el-color-info);
      background: var(--el-fill-color-light);
    }
    &.tag-warning{
      color: var(--el-color-warning);
      background: var(--el-color-warning-light-9);
    }
    &.tag-success{
      color: var(--el-color-success);
      background: var(--el-color-success-light-9);
    }
    &.tag-danger{
      color: var(--el-color-danger);
      background: var(--el-color-danger-light-9);
    }
  }
  // 跟进记录
  .follow-item{
    padding: 10px 0;
    border-bottom: 1px solid var(--yq-border-color);
    &:first-child{
      padding-top: 0;
    }
    &:last-child{
      border-bottom: none;
      padding-bottom: 0;
    }
    .follow-head{
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: var(--yq-font-size-mini-mobile);
      .who{
        color: var(--yq-font-color-base);
        font-weight: 600;
      }
      .time{
        margin-left: auto;
        color: var(--yq-font-color-desc);
      }
    }
    // 跟进内容是多行文本（textarea 录入）：保留换行、不做截断
    // 样式与客户跟进列表保持一致（灰底块）
    .follow-content{
      margin-top: 6px;
      padding: 10px;
      border-radius: 4px;
      background: var(--yq-background-color);
      font-size: var(--yq-font-size-mini-mobile);
      color: var(--yq-font-color-tips);
      line-height: 1.6;
      white-space: pre-wrap;
      word-break: break-all;
      overflow-wrap: anywhere;
    }
    .follow-next{
      margin-top: 4px;
      font-size: var(--yq-font-size-mini-mobile);
      color: var(--yq-font-color-desc);
    }
  }
  // 选择类控件（阶段/来源）放在 data-input 行里：去掉自带边框/箭头，字号跟随整行
  :deep(.row-select .select-area),
  :deep(.condition-area){
    width: auto;
    box-shadow: none;
    padding: 0;
    background: transparent;
    font-size: inherit;
    color: var(--yq-font-color-base);
    .value{
      flex: none;
      width: auto;
      max-width: 220px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      text-align: right;
      &.placeholder{
        color: var(--yq-font-color-desc);
      }
    }
    // 箭头交给 data-input 统一渲染，控件自带的那个关掉
    .arrow-down{
      display: none;
    }
  }
}
</style>
